import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import tinycolor2 from 'tinycolor2';
import { IconChevronLeft } from '@tabler/icons-react';
import { BrandKitColorPickerContent } from './BrandKitColorPickerContent';
import { classnames } from '@/libs/utils';
import { useDynamicPopupPosition } from '@/hooks/useDynamicPopupPosition';

interface BrandKitColorPickerProps {
  selectedColor: string;
  onColorSelect: (color: string) => void;
  onClose?: () => void;
  title?: string;
  children?: React.ReactNode;
  classNames?: string;
  openClassNames?: string;
  pickerClassNames?: string;
  useDynamicPosition?: boolean;
  /**
   * When provided, clicking the trigger calls this instead of opening the floating panel.
   * Used by parent popovers to implement the panel-swap back-button pattern.
   */
  onOpen?: () => void;
  /** Render the picker content inline (no trigger div, no floating wrapper). */
  inline?: boolean;
  /** When inline is true, shows a ← button that calls this handler. */
  onBack?: () => void;
}

export default function BrandKitColorPicker({
  selectedColor,
  onColorSelect,
  onClose,
  title = 'Font Colour',
  children,
  classNames = '',
  openClassNames = '',
  pickerClassNames = '',
  useDynamicPosition = false,
  onOpen,
  inline = false,
  onBack
}: BrandKitColorPickerProps): JSX.Element {
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const pickerColor = useMemo(() => (tinycolor2(selectedColor).isDark() ? 'white' : 'black'), [selectedColor]);

  const { position, isPositioned } = useDynamicPopupPosition({
    triggerRef,
    popupRef: panelRef,
    isVisible: isOpen && useDynamicPosition,
    preferredPosition: 'right',
    offset: 8,
    viewportPadding: 16
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    function handlePointerUp(e: PointerEvent) {
      const isOutside = !panelRef.current?.contains(e.target as Node);
      if (isOutside) {
        setIsOpen(false);
        onClose?.();
        document.removeEventListener('pointerup', handlePointerUp);
      }
    }
    document.addEventListener('pointerup', handlePointerUp);
    return () => document.removeEventListener('pointerup', handlePointerUp);
  }, [isOpen, onClose]);

  const openPicker = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onOpen) {
        onOpen();
      } else {
        setIsOpen(true);
      }
    },
    [onOpen]
  );

  if (inline) {
    return (
      <div className="p-4">
        <div className="mb-2 flex items-center gap-1">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-1 rounded-md p-1 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
            >
              <IconChevronLeft size={16} />
            </button>
          )}
          {title && <h3 className="text-xs text-slate-950">{title}</h3>}
        </div>
        <BrandKitColorPickerContent selectedColor={selectedColor} onColorSelect={onColorSelect} onBack={onBack} />
      </div>
    );
  }

  return (
    <>
      <div
        ref={triggerRef}
        data-testid="color-picker"
        className={classnames(
          'size-full cursor-pointer border border-slate-200',
          classNames,
          isOpen ? openClassNames : ''
        )}
        style={{ backgroundColor: selectedColor, color: pickerColor }}
        onClick={openPicker}
      >
        {children}
      </div>

      {isOpen && (
        <div
          ref={panelRef}
          data-testid="brand-kit-color-picker-panel"
          className={classnames(useDynamicPosition ? 'fixed' : 'absolute', 'z-50', pickerClassNames)}
          style={{
            ...(useDynamicPosition ? position : {}),
            opacity: useDynamicPosition ? (isPositioned ? 1 : 0) : 1,
            transition: 'opacity 150ms ease-in-out'
          }}
        >
          <div className="w-[240px] rounded-lg border border-slate-200 bg-white p-4 shadow-xl">
            {title && (
              <div className="mb-2">
                <h3 className="text-xs text-slate-950">{title}</h3>
              </div>
            )}
            <BrandKitColorPickerContent selectedColor={selectedColor} onColorSelect={onColorSelect} />
          </div>
        </div>
      )}
    </>
  );
}
